import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
    useLoggedIn,
    useRequestUrl,
    useAuthToken,
    useCourtsByAgency,
    useAgencyCourt,
    useCurrentUser,
    useApp,
    useAuthentication,
    useCitepayUser,
    useShowPasswordDialog
} from "../context/contextHooks";
import { useForm } from "react-hook-form";
import API from "../http/API";
import Grid2 from "@mui/material/Grid2";
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { AlertTitle } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import PasswordReset from './PasswordReset'
import sendPost from "../http/sendRequests";
import Mfa from './Mfa';
import {useSxStyles as sx} from "../styles/sxStyles";


const Login = () => {
    const navigate = useNavigate();
    const [requestUrl] = useRequestUrl()
    const { register, handleSubmit } = useForm();
    const [, setAuthentication] = useAuthentication();
    const citePayAdmin = useCitepayUser();
    const [isLoggedIn,] = useLoggedIn();
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [, setShowPasswordDialog] = useShowPasswordDialog();
    const [currentUser] = useCurrentUser();
    const [authToken] = useAuthToken();
    const [, setCourtsByAgency] = useCourtsByAgency();
    const [, setAgencyCourt] = useAgencyCourt();
    const [app, setApp] = useApp();
    const [credentials, setCredentials] = useState(null);
    const [openMfa, setOpenMfa] = useState(false)
    const [mfaFields, setMfaFields] = useState(null);
    const [codeSent, setCodeSent] = useState(false);


    const [actionResponse, setActionResponse] = useState(null);

    const getCourtsByAgency = () => {
        const url = `${requestUrl}/courtsByAgency`;
        const request = { "agencyId": currentUser.agencyId };
        sendPost(url, request, authToken, setCourtsByAgency, setErrorMessage)
    }

    const onSubmitForm = (form) => {
        setAgencyCourt(null);
        const loginFields = {username: form.user, password: form.pwd}
        if (app && app.requireMfa) {
            setCodeSent(false);
            setCredentials(loginFields);
            getMfaFields(loginFields);
            setOpenMfa(true);
        } else {
            sendLogin(loginFields);
        }
    }

    const sendLogin = (loginFields) => {
        setCodeSent(false);
        setLoading(true);
        setErrorMessage(null);
        API.login(`${requestUrl}/login`, loginFields, setAuthentication, setErrorMessage);
    }

    const getApp = () => {
        setLoading(true);
        setActionResponse(null);
        API.getRetrieve(`${requestUrl}/getApp`, 'getApp', null, setActionResponse);
    }

    const getMfaFields = (loginFields) => {
        setLoading(true);
        setErrorMessage(null);
        API.postRetrieve(`${requestUrl}/getMfaFields`, loginFields, 'getMfaFields', null, setActionResponse);
    }

    const sendMfaCode = (loginFields) => {
        setLoading(true);
        setErrorMessage(null);
        API.postRetrieve(`${requestUrl}/sendMfaCode`, JSON.stringify(loginFields), 'sendMfaCode', null, setActionResponse);
    }

    const togglePassword = () => {
        setShowPassword(!showPassword);
    }

    const disableMouseDown = (e) => {
        e.preventDefault();
    }

    const resetPassword = () => {
        setShowPasswordDialog(true)
    }

    useEffect(() => {
        if (!app) {
            getApp();
        }
    }, [app])

    useEffect(() => {
        if (isLoggedIn) {
            getCourtsByAgency();
            (citePayAdmin || currentUser.isAdmin) ? navigate('/users') : navigate('/lookup');
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (errorMessage) setLoading(false);
    }, [errorMessage])

    useEffect(() => {
        if (actionResponse && loading) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'getApp') {
                    setApp(actionResponse.data)
                } else if (actionResponse.requestType === 'getMfaFields') {
                    setMfaFields(actionResponse.data);
                } else if (actionResponse.requestType === 'sendMfaCode') {
                    setCodeSent(true);
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    return (
        <Grid2
            container
            spacing={3}
            direction="column"
            sx={{ maxHeight: '100vh', ...sx.login }}
        >
            {openMfa && mfaFields &&
                <Mfa
                    openMfa={openMfa}
                    setOpenMfa={setOpenMfa}
                    credentials={credentials}
                    mfaFields={mfaFields}
                    sendMfaCode={sendMfaCode}
                    codeSent={codeSent}
                    sendLogin={sendLogin}
                />
            }
            <Grid2 display='flex' justifyContent='center' alignItems='center' size={12} >
                <Paper elevation={3} sx={{maxWidth: '500px', padding: '1rem 0'}} >
                    <Grid2 container >
                    {errorMessage &&
                        <Alert severity="error">
                            <AlertTitle>Login Failed</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    { actionResponse && actionResponse.error &&
                        <Alert severity="error">
                            <AlertTitle>Error</AlertTitle>
                            {actionResponse.errorMessage}
                        </Alert>
                    }
                        <Grid2 size={12} sx={{...sx.root, padding: '0 1rem'}}>
                            <FormControl fullWidth {...register('user')}>
                                <FormLabel shrink htmlFor='user' sx={sx.inputLabel}>Username</FormLabel>
                                <TextField
                                    id="user"
                                    name="user"
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle />
                                                </InputAdornment>
                                            )
                                        },
                                        htmlInput: {sx: sx.textbox}
                                    }}
                                />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={12} sx={{...sx.root, padding: '0 1rem'}}>
                            <FormControl fullWidth {...register('pwd')}>
                                <FormLabel shrink htmlFor='pwd' sx={sx.inputLabel}>Password</FormLabel>
                                <TextField
                                    id="pwd"
                                    name="pwd"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={togglePassword}
                                                        onMouseDown={disableMouseDown}
                                                    >
                                                        { showPassword ? <Visibility/> : <VisibilityOff/> }
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        },
                                        htmlInput: {sx: sx.textbox}
                                    }}
                                />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={12} sx={{...sx.root, padding: '0 1rem'}}>
                            <Button variant="contained" color="primary" sx={sx.button} onClick={handleSubmit(onSubmitForm)} >
                                Login
                            </Button>
                            <Button onClick={resetPassword} color="primary">RESET PASSWORD</Button>
                        </Grid2>
                    </Grid2>
                </Paper>
            </Grid2>
            <PasswordReset />
            <Backdrop sx={sx.backdrop} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Grid2>
    );
}

export default Login;
